import React, { useState, useEffect } from 'react';
import { X, Download, Share } from 'lucide-react';

const InstallAppBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed or dismissed
    const dismissed = localStorage.getItem('bitz_install_dismissed');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone || dismissed === 'true') return;

    // Check iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIOS(isiOS);

    // Show banner after 3 seconds for iOS (no beforeinstallprompt on Safari)
    if (isiOS) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Show banner on any device after 5s (mobile-width screens or mobile UA)
    const isMobileWidth = window.innerWidth <= 768;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    let fallbackTimer;
    if (isMobileWidth || isMobileUA) {
      fallbackTimer = setTimeout(() => setShowBanner(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('bitz_install_dismissed', 'true');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 animate-slide-up" data-testid="install-app-banner">
      <div className="relative max-w-md mx-auto bg-gradient-to-r from-[#1A1A1C] to-[#252527] border border-[#D4AF37]/30 rounded-2xl p-4 shadow-2xl shadow-black/50">
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install banner"
          data-testid="install-app-dismiss"
          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8F741F] flex items-center justify-center flex-shrink-0">
            <img src="/icons/icon-96x96.png" alt="BITZ" className="w-10 h-10 rounded-lg" onError={(e) => { e.target.style.display='none' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-bold text-sm">Install BITZ Club App</h3>
            <p className="text-gray-400 text-xs mt-0.5">Quick access to membership, bookings & offers</p>
          </div>
        </div>

        {isIOS ? (
          <div className="mt-3 p-2.5 bg-white/5 rounded-lg">
            <p className="text-gray-300 text-xs">
              Tap <Share className="w-3.5 h-3.5 inline text-blue-400 mx-0.5" /> then <strong>"Add to Home Screen"</strong>
            </p>
          </div>
        ) : (
          <button
            onClick={deferredPrompt ? handleInstall : handleDismiss}
            className="mt-3 w-full py-2.5 bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-black font-bold text-sm rounded-lg flex items-center justify-center gap-2 active:scale-95 transition-transform"
            data-testid="install-app-btn"
          >
            <Download className="w-4 h-4" />
            {deferredPrompt ? 'Install App' : 'Add to Home Screen'}
          </button>
        )}
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default InstallAppBanner;
